import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Divider,
  IconButton,
  Autocomplete,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignUserSelect from './AssignUserSelect';
import DiscussionTextarea from './DiscussionTextarea';
import StatusChip from './StatusChip';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

/**
 * CreateEntityDialog Component
 * Reusable dialog for creating new entities (Enquiry, Quotation, Order)
 * Config-driven form fields with validation
 */
const CreateEntityDialog = ({
  open,
  onClose,
  onSubmit,
  title,
  fields = [],
  initialValues = {},
  relatedEntityOptions = {} // For dropdowns that depend on other entities
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState(initialValues);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setFormData(initialValues);
      setErrors({});
    }
  }, [open, initialValues]);

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when field is changed
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    fields.forEach(field => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const renderField = (field) => {
    const value = formData[field.id] || '';
    const error = errors[field.id];

    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field.id}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            fullWidth
            size="small"
            required={field.required}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          />
        );

      case 'number':
        return (
          <TextField
            key={field.id}
            label={field.label}
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            fullWidth
            size="small"
            required={field.required}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        );

      case 'select':
        const options = field.options || [];
        return (
          <TextField
            key={field.id}
            select
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            fullWidth
            size="small"
            required={field.required}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          >
            {options.map((option) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              return (
                <MenuItem key={optionValue} value={optionValue}>
                  {field.showStatusChip && typeof optionValue === 'string' ? (
                    <StatusChip status={optionValue} />
                  ) : (
                    optionLabel
                  )}
                </MenuItem>
              );
            })}
          </TextField>
        );

      case 'autocomplete':
        const autocompleteOptions = relatedEntityOptions[field.id] || [];
        return (
          <Autocomplete
            key={field.id}
            options={autocompleteOptions}
            getOptionLabel={(option) => option.label || option}
            value={autocompleteOptions.find(opt => opt.value === value) || null}
            onChange={(e, newValue) => handleFieldChange(field.id, newValue?.value || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                size="small"
                required={field.required}
                error={!!error}
                helperText={error}
                sx={{ mb: 2 }}
              />
            )}
            fullWidth
          />
        );

      case 'date':
        return (
          <LocalizationProvider key={field.id} dateAdapter={AdapterDayjs}>
            <DatePicker
              label={field.label}
              value={value ? dayjs(value) : null}
              onChange={(newValue) => {
                const dateValue = newValue ? newValue.toISOString().split('T')[0] : null;
                handleFieldChange(field.id, dateValue);
              }}
              slotProps={{ textField: { 
                size: 'small', 
                fullWidth: true, 
                required: field.required,
                error: !!error,
                helperText: error,
                sx: { mb: 2 }
              } }}
            />
          </LocalizationProvider>
        );

      case 'assignee':
        return (
          <AssignUserSelect
            key={field.id}
            value={value}
            onChange={(val) => handleFieldChange(field.id, val)}
          />
        );

      case 'textarea':
        return (
          <DiscussionTextarea
            key={field.id}
            label={field.label}
            value={value}
            onChange={(val) => handleFieldChange(field.id, val)}
          />
        );

      case 'dependentSelect':
        // Select that depends on another field value
        const dependentOptions = field.getOptions 
          ? field.getOptions(formData[field.dependsOn]) 
          : [];
        
        return (
          <TextField
            key={field.id}
            select
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            fullWidth
            size="small"
            required={field.required}
            error={!!error}
            helperText={error}
            disabled={!formData[field.dependsOn]}
            sx={{ mb: 2 }}
          >
            {dependentOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: '90vh' },
        }
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <DialogTitle sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' }, fontWeight: 600, p: 0 }}>
            {title}
          </DialogTitle>
          <IconButton
            size={isMobile ? 'medium' : 'small'}
            onClick={onClose}
            sx={{
              padding: { xs: '8px', sm: '4px' },
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 0, pt: 1 }}>
            {fields.map((field) => renderField(field))}
          </DialogContent>
          
          <DialogActions sx={{ 
            px: 0, 
            pb: { xs: 1, sm: 0 }, 
            pt: { xs: 2, sm: 2 }, 
            gap: { xs: 1, sm: 1.5 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
              margin: { xs: '0 !important', sm: 'initial' },
            }
          }}>
            <Button
              onClick={onClose}
              variant="outlined"
              size={isMobile ? 'medium' : 'small'}
              sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size={isMobile ? 'medium' : 'small'}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                backgroundColor: 'secondary.main',
                color: 'secondary.contrastText',
                '&:hover': {
                  backgroundColor: 'secondary.dark',
                },
              }}
            >
              Create
            </Button>
          </DialogActions>
        </form>
      </Box>
    </Dialog>
  );
};

export default CreateEntityDialog;

